import { queryKeys } from '@/shared/constants/query-keys'

/** Lo mínimo que se necesita de React Query, para no atarse a su tipo en una función de dominio. */
type Invalidator = { invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown }

/**
 * Qué hay que refrescar después de mover un expediente de estado.
 *
 * ── EL BUG QUE MOTIVÓ EXTRAER ESTO ──────────────────────────────────────────
 *
 * Las cinco transiciones invalidaban **una sola clave**, `negotiations.detail(id)` — la de v1,
 * indexada por `_id`. Hoy esa clave tiene **un solo lector**: el panel lateral del tablero.
 *
 * Todo lo demás de la pantalla donde viven los botones lee claves de v2:
 *
 * | Qué se ve | Clave | ¿Se invalidaba? |
 * | --- | --- | --- |
 * | Las vistas del expediente | `detailV2`, `formsV2` | ❌ |
 * | **La pestaña de historial** | `historyV2` | ❌ |
 * | El tablero, al volver | `adminListV2`, `summaryV2` | ❌ |
 *
 * O sea: rechazabas un expediente, el servidor lo movía, y **el historial seguía sin mostrar la
 * transición que acababas de hacer**. El `// navigate(0)` comentado en cuatro de las cinco
 * acciones es el fósil de eso — alguien recargó la página entera para que se viera.
 *
 * Lo llamativo es que el arreglo ya estaba escrito al lado: `action-save-negotiation` —el guardado
 * de la EDICIÓN— invalida las tres claves v2 desde que se migró. Sólo que las transiciones se
 * escribieron antes y nadie volvió a mirarlas.
 *
 * ── POR QUÉ SE INVALIDA LA FAMILIA ENTERA ───────────────────────────────────
 *
 * `queryKeys.negotiations.all` es `['negotiations']`, prefijo de TODAS las de arriba. Se eligió
 * eso y no enumerar cinco claves por una razón concreta: **enumerar es lo que falló**. Una lista
 * de claves hay que acordarse de ampliarla cada vez que nace una pantalla, y el día que alguien
 * se olvide el síntoma es este mismo — datos viejos, sin error.
 *
 * El costo está acotado: React Query sólo REFETCHEA lo que está montado; el resto queda marcado
 * como viejo y se pide cuando haga falta. Y una transición cambia el estado del expediente, que
 * es justamente el dato que pinta cada una de esas superficies.
 *
 * ── POR QUÉ UNA FUNCIÓN Y NO CINCO COPIAS ───────────────────────────────────
 *
 * Porque cinco copias fue exactamente el problema. Esta misma corrida encontró un secreto de
 * cursor copiado en diecisiete servicios donde la copia dieciocho salió distinta y firmó con la
 * cadena vacía: **un consenso que no se ejecuta no es un guardarraíl.**
 */
export function invalidateNegotiationAfterTransition(queryClient: Invalidator): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.negotiations.all })
}
